import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  type CreateDepartmentInput,
  type UpdateDepartmentInput,
} from '../lib/departmentsApi';

// CLE-DASH — departments. The shared `['departments']` key feeds the Settings
// tab, task/people filters and the create-member modal. Mutations also
// invalidate people + tasks: both embed the department name/color.

export const DEPARTMENTS_KEY = ['departments'] as const;

export function useDepartments() {
  return useQuery({
    queryKey: DEPARTMENTS_KEY,
    queryFn: listDepartments,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateDepartments() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
    void qc.invalidateQueries({ queryKey: ['people'] });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  };
}

export function useCreateDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: (input: CreateDepartmentInput) => createDepartment(input),
    onSuccess: invalidate,
  });
}

export function useUpdateDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDepartmentInput }) =>
      updateDepartment(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({
    mutationFn: (id: string) => deleteDepartment(id),
    onSuccess: invalidate,
  });
}
